import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Business,
  LocationOn,
  Devices,
  CheckCircle,
  Cancel,
  Warning,
  Settings,
  NotificationsActive,
  MonitorHeart,
  HeartBroken,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import FleetMap from '../../components/FleetMap';
import { useFleetOverview, useFleetMap, useFleetAlerts, usePlayerHealth } from '../../hooks/useFleet';
import { SectionTabs } from '../../components/SectionTabs';
import { FLEET_TABS, FLEET_ACCENT_COLOR } from './fleetTabs';
import { fleetHelp } from '../../components/PageHelp/helpContent';

const FleetDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: overview, isLoading: overviewLoading, error: overviewError } = useFleetOverview();
  const { data: mapData, isLoading: mapLoading } = useFleetMap();
  const { data: recentAlerts = [], isLoading: alertsLoading } = useFleetAlerts(24);
  // Player app installs (PWA self-reporting), separate from provisioned hardware.
  const { data: playerHealth } = usePlayerHealth({ limit: 500 });
  const players = playerHealth?.summary;

  if (overviewLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (overviewError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load fleet data: {(overviewError as Error)?.message || 'Unknown error'}
      </Alert>
    );
  }

  const stats = [
    {
      title: 'Organizations',
      value: overview?.organizations || 0,
      icon: <Business sx={{ color: '#6366f1' }} />,
      color: '#6366f1',
      onClick: () => navigate('/fleet/organizations'),
    },
    {
      title: 'Sites',
      value: overview?.sites || 0,
      icon: <LocationOn sx={{ color: '#8b5cf6' }} />,
      color: '#8b5cf6',
      onClick: () => navigate('/fleet/sites'),
    },
    {
      title: 'Total Devices',
      value: overview?.devices?.total || 0,
      icon: <Devices sx={{ color: '#4a9eff' }} />,
      color: '#4a9eff',
      onClick: () => navigate('/fleet/devices'),
    },
    {
      title: 'Online',
      value: overview?.devices?.online || 0,
      icon: <CheckCircle sx={{ color: '#10b981' }} />,
      color: '#10b981',
      onClick: () => navigate('/fleet/devices?status=online'),
    },
    {
      title: 'Offline',
      value: overview?.devices?.offline || 0,
      icon: <Cancel sx={{ color: '#ef4444' }} />,
      color: '#ef4444',
      onClick: () => navigate('/fleet/devices?status=offline'),
    },
    {
      title: 'Alerts (24h)',
      value: overview?.alerts_24h || 0,
      icon: <NotificationsActive sx={{ color: '#f59e0b' }} />,
      color: '#f59e0b',
      onClick: () => navigate('/fleet/alerts'),
    },
    {
      title: 'Players Alive',
      value: players ? `${players.alive} / ${players.total}` : '-',
      icon: <MonitorHeart sx={{ color: '#10b981' }} />,
      color: '#10b981',
      onClick: () => navigate('/fleet/player-health'),
    },
    {
      title: 'Players Down',
      value: players ? players.stale + players.dead : '-',
      icon: <HeartBroken sx={{ color: '#ef4444' }} />,
      color: '#ef4444',
      onClick: () => navigate('/fleet/player-health?status=dead'),
    },
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#ef4444';
      case 'error': return '#f87171';
      case 'warning': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'error':
        return <Cancel sx={{ color: getSeverityColor(severity) }} />;
      case 'warning':
        return <Warning sx={{ color: getSeverityColor(severity) }} />;
      default:
        return <Settings sx={{ color: getSeverityColor(severity) }} />;
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hr ago`;
    return date.toLocaleDateString();
  };

  return (
    <Box>
      <SectionTabs
        title="Fleet Management"
        tabs={FLEET_TABS}
        accentColor={FLEET_ACCENT_COLOR}
        help={fleetHelp.overview}
      />

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, index) => (
          <Grid item xs={6} sm={4} md={2} key={index}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
              }}
              onClick={stat.onClick}
            >
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Box sx={{ mb: 1 }}>{stat.icon}</Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Map */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Device Locations
            </Typography>
            {mapLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
              </Box>
            ) : (
              <FleetMap
                sites={mapData?.sites || []}
                devices={mapData?.devices || []}
                height={450}
                onSiteClick={(site) => navigate(`/fleet/sites/${site.id}`)}
                onDeviceClick={(device) => navigate(`/fleet/devices/${device.id}`)}
              />
            )}
          </Paper>
        </Grid>

        {/* Recent Alerts */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, height: '100%', maxHeight: 530, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Recent Alerts
              </Typography>
              <Chip
                label={`${recentAlerts.length} alerts`}
                size="small"
                sx={{ bgcolor: recentAlerts.length > 0 ? '#f59e0b20' : '#10b98120', color: recentAlerts.length > 0 ? '#f59e0b' : '#10b981' }}
              />
            </Box>

            {alertsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : recentAlerts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircle sx={{ fontSize: 48, color: '#10b981', mb: 1 }} />
                <Typography color="text.secondary">
                  No alerts in the last 24 hours
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {recentAlerts.slice(0, 10).map((alert, index) => (
                  <React.Fragment key={alert.id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      sx={{ px: 0, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/fleet/devices/${alert.device_id}`)}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getSeverityIcon(alert.severity)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {alert.device_name || 'Unknown Device'}
                            </Typography>
                            <Chip
                              label={alert.event_type}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: `${getSeverityColor(alert.severity)}20`,
                                color: getSeverityColor(alert.severity),
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {alert.site_name || 'No site'} - {alert.organization_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(alert.created_at)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Device Status Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Device Status Distribution
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#10b981', mr: 1 }} />
                  <Typography variant="body2">Online</Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {overview?.devices?.online || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444', mr: 1 }} />
                  <Typography variant="body2">Offline</Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {overview?.devices?.offline || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f59e0b', mr: 1 }} />
                  <Typography variant="body2">Error</Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {overview?.devices?.error || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#6366f1', mr: 1 }} />
                  <Typography variant="body2">Provisioning</Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {overview?.devices?.provisioning || 0}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ width: 120, height: 120, position: 'relative' }}>
                {/* Simple pie chart visualization */}
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: overview?.devices?.total
                      ? `conic-gradient(
                          #10b981 0deg ${(overview.devices.online / overview.devices.total) * 360}deg,
                          #ef4444 ${(overview.devices.online / overview.devices.total) * 360}deg ${((overview.devices.online + overview.devices.offline) / overview.devices.total) * 360}deg,
                          #f59e0b ${((overview.devices.online + overview.devices.offline) / overview.devices.total) * 360}deg ${((overview.devices.online + overview.devices.offline + overview.devices.error) / overview.devices.total) * 360}deg,
                          #6366f1 ${((overview.devices.online + overview.devices.offline + overview.devices.error) / overview.devices.total) * 360}deg 360deg
                        )`
                      : '#333',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {overview?.devices?.total || 0}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => navigate('/fleet/provisioning')}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Settings sx={{ fontSize: 32, color: '#4a9eff', mb: 1 }} />
                    <Typography variant="body2">Provision Device</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => navigate('/fleet/sites/new')}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <LocationOn sx={{ fontSize: 32, color: '#8b5cf6', mb: 1 }} />
                    <Typography variant="body2">Add Site</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => navigate('/fleet/alerts')}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <NotificationsActive sx={{ fontSize: 32, color: '#f59e0b', mb: 1 }} />
                    <Typography variant="body2">Manage Alerts</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => navigate('/fleet/devices?status=offline')}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Cancel sx={{ fontSize: 32, color: '#ef4444', mb: 1 }} />
                    <Typography variant="body2">View Offline</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FleetDashboard;
